import {
  DELETE_SONG,
  GET_PLAYLIST,
  GET_PLAYLISTS,
  GET_SONGS,
} from 'api/graphql'
import { getUserDisplayName, hasSongPermission } from 'utils'
import { useMutation, useQuery, useReactiveVar } from '@apollo/client'

import { DBSongType } from 'types'
import FloatingButton from 'components/App/FloatingMenu/FloatingButton'
import { LOGGED_USER_VAR } from 'App'
import SvgIcon from 'components/SvgIcon'
import confirmedAction from 'modals/actions/confirmedAction'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const Button = styled.button`
  width: 100%;
  min-width: 150px;
  max-width: 300px;
  padding-right: 25px;
  align-self: center;
  background: ${(props) => props.theme.warning} !important;
`

const SongButtonDelete = (props: {
  song: DBSongType
  onDelete: () => void
  isFloatingButton?: boolean
  isVisible?: boolean
  isIndented?: boolean
  noShadow?: boolean
  tooltipShortIsVisible?: boolean
  index?: number
}) => {
  const { t } = useTranslation()
  const loggedUser = useReactiveVar(LOGGED_USER_VAR)

  const getAllowDeleteSong = () =>
    hasSongPermission(props.song?.songMembers, 'SONG_deleteSong')

  //
  // DELETE SONG
  //
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: dataSongs } = useQuery(GET_SONGS) /* NECESSARY FOR REFETCH */

  const [deleteSong, { data: delData }] = useMutation(DELETE_SONG, {
    refetchQueries: [GET_SONGS, GET_PLAYLISTS, GET_PLAYLIST],
  })

  useEffect(() => {
    if (delData?.deleteSong) {
      if (delData?.deleteSong?.success) {
        toast.success(t(delData?.deleteSong?.message))
        props.onDelete()
      } else {
        toast.error(t(delData?.deleteSong?.message))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delData])

  const safelyDeleteSong = () => {
    const members = []
    props.song.songMembers
      .filter((m) => m.user?.id !== loggedUser.id)
      .forEach((m) => {
        if (m.user?.name) {
          const name = getUserDisplayName(m.user)
          if (!members.includes(name)) {
            members.push(name)
          }
        } else if (m.userGroup) {
          m.userGroup.userGroupMembers?.forEach((gm) => {
            const name = getUserDisplayName(gm.user)
            if (!members.includes(name)) {
              members.push(name)
            }
          })
        }
      })

    const memberList = members.map((m) => `'${m}'`).join('\n')

    confirmedAction({
      title: t('song.delete.title'),
      description: t('song.delete.description').replace(
        '{SONG}',
        props.song.name
      ),
      descriptionWarning:
        memberList.length > 0 &&
        t('song.delete.description users').replace(
          '{USERS/GROUPS}',
          memberList
        ),
      okText:
        memberList.length > 0
          ? t('song.delete.yes for all')
          : t('song.delete.yes'),
      cancelText: t('song.delete.no'),
      onSubmit: () => {
        deleteSong({ variables: { id: props.song.id } })
      },
    })
  }

  //
  // BUTTON CLICK
  //
  const onDeleteClick = () => {
    safelyDeleteSong()
  }

  //
  // RENDER
  //
  const renderButton = () => {
    if (props.isFloatingButton) {
      return (
        <FloatingButton
          button={{
            onClick: onDeleteClick,
            tooltip: t('song.delete.label'),
            tooltipShort: t('song.delete.label short'),
            tooltipShortIsVisible: props.tooltipShortIsVisible,
            icon: <SvgIcon code="icon-delete" />,
            color: 'warning',
            isVisible: props.isVisible,
            isIndented: props.isIndented,
          }}
          noShadow={props.noShadow}
          index={props.index}
        />
      )
    }
    return (
      <Button className="btn-primary" onClick={onDeleteClick}>
        <SvgIcon code="icon-delete" /> {t('song.delete.label')}
      </Button>
    )
  }

  return getAllowDeleteSong() ? renderButton() : null
}

export default SongButtonDelete
